import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { 
  Trophy, CheckCircle2, ArrowRight, Scale, Dumbbell, Heart, 
  TrendingUp, TrendingDown, Target, Loader2, Sparkles, 
  BarChart3, Flame, Brain, Moon, Download, Share2, ChevronDown,
  Award, Zap, BookOpen, Shield, Activity, Clock
} from 'lucide-react';
import html2canvas from 'html2canvas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MesocycleReport = ({ onClose, onAdvance }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [showFullReport, setShowFullReport] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/me/mesocycle-full-report`);
      setReport(response.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar el informe');
    } finally {
      setLoading(false);
    }
  };

  const handleFinalize = async () => {
    if (!report?.can_finalize) {
      toast.error(`Necesitas completar al menos el 80%. Actual: ${report?.progress_percent}%`);
      return;
    }

    setFinalizing(true);
    try {
      const response = await axios.post(`${API}/me/finalize-mesocycle`);
      toast.success(response.data.message);
      if (onAdvance) onAdvance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al finalizar');
    } finally {
      setFinalizing(false);
    }
  };

  const downloadReport = async () => {
    if (!reportRef.current) return;
    
    try {
      toast.loading('Generando PDF...');
      
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        backgroundColor: '#18181b',
        useCORS: true
      });
      
      const link = document.createElement('a');
      link.download = `informe-mesociclo-${report?.mesocycle || 1}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.dismiss();
      toast.success('Informe descargado');
    } catch (error) {
      toast.dismiss();
      toast.error('Error al descargar');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="text-muted-foreground">Generando tu informe...</p>
      </div>
    );
  }

  if (!report || report.error) {
    return (
      <div className="text-center p-8">
        <p className="text-muted-foreground">{report?.error || 'No se pudo generar el informe'}</p>
        <Button onClick={onClose} className="mt-4">Cerrar</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 max-w-2xl mx-auto pb-24">
      {/* Header con Logo GuardiansFit */}
      <div className="text-center space-y-3" ref={reportRef}>
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary to-amber-500 mb-2">
          <Shield className="h-8 w-8 text-black" />
        </div>
        <h1 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-400">
          GUARDIANSFIT
        </h1>
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 rounded-full">
          <Trophy className="h-5 w-5 text-primary" />
          <span className="text-lg font-bold text-primary">
            Informe Mesociclo {report.mesocycle}
          </span>
        </div>
        <p className="text-muted-foreground">{report.plan_name}</p>
        
        {/* Mensaje Motivacional Principal */}
        <Card className="bg-gradient-to-r from-primary/20 to-amber-500/20 border-primary/30 mt-4">
          <CardContent className="py-4">
            <p className="text-lg text-foreground font-medium">
              {report.motivational_message}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Puntuación Global */}
      <Card className="bg-gradient-to-br from-zinc-900 to-zinc-800 border-zinc-700 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent" />
        <CardContent className="pt-6 relative">
          <div className="text-center mb-4">
            <div className="relative inline-block">
              <div className="text-7xl font-black text-transparent bg-clip-text bg-gradient-to-b from-primary to-amber-500">
                {report.global_score}
              </div>
              <div className="absolute -top-2 -right-4">
                <span className="text-2xl">/100</span>
              </div>
            </div>
            <p className="text-muted-foreground mt-2">Puntuación del Mesociclo</p>
          </div>
          
          {/* Desglose de Puntuación */}
          <div className="grid grid-cols-4 gap-2 mt-4">
            {report.score_breakdown?.map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="text-lg font-bold text-foreground">{item.score}</div>
                <div className="text-[10px] text-muted-foreground">{item.label}</div>
                <div className="text-[9px] text-primary/70">{item.weight}%</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Logros Desbloqueados */}
      {report.all_achievements?.length > 0 && (
        <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-amber-400 flex items-center gap-2">
              <Award className="h-5 w-5" />
              LOGROS DESBLOQUEADOS
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {report.all_achievements.map((achievement, idx) => (
              <div 
                key={idx} 
                className="flex items-center gap-3 p-3 bg-black/30 rounded-lg border border-amber-500/20"
              >
                <span className="text-2xl">{achievement.icon}</span>
                <span className="text-foreground text-sm">{achievement.text}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* SECCIÓN CUERPO */}
      {report.body?.has_data && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-green-400 flex items-center gap-2">
              <Activity className="h-5 w-5" />
              CUERPO - Cambios Fisiológicos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {/* Inicio vs Actual */}
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground text-center">INICIO</p>
                <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">{report.body.initial?.weight || '-'}</p>
                  <p className="text-xs text-muted-foreground">kg</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">{report.body.initial?.body_fat || '-'}</p>
                  <p className="text-xs text-muted-foreground">% grasa</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">{report.body.initial?.muscle_mass || '-'}</p>
                  <p className="text-xs text-muted-foreground">% músculo</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <p className="text-xs text-green-400 text-center">AHORA</p>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-foreground">{report.body.current?.weight || report.body.initial?.weight || '-'}</p>
                  {report.body.changes?.weight && (
                    <p className={`text-sm font-bold ${report.body.changes.weight <= 0 ? 'text-green-400' : 'text-yellow-400'}`}>
                      {report.body.changes.weight > 0 ? '+' : ''}{report.body.changes.weight} kg
                    </p>
                  )}
                </div>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-foreground">{report.body.current?.body_fat || report.body.initial?.body_fat || '-'}</p>
                  {report.body.changes?.body_fat && (
                    <p className={`text-sm font-bold ${report.body.changes.body_fat <= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {report.body.changes.body_fat > 0 ? '+' : ''}{report.body.changes.body_fat}%
                    </p>
                  )}
                </div>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-foreground">{report.body.current?.muscle_mass || report.body.initial?.muscle_mass || '-'}</p>
                  {report.body.changes?.muscle_mass && (
                    <p className={`text-sm font-bold ${report.body.changes.muscle_mass >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {report.body.changes.muscle_mass > 0 ? '+' : ''}{report.body.changes.muscle_mass}%
                    </p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* SECCIÓN FUERZA */}
      <Card className="bg-gradient-to-br from-cyan-500/10 to-blue-500/5 border-cyan-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-cyan-400 flex items-center gap-2">
            <Dumbbell className="h-5 w-5" />
            FUERZA - Volumen de Entrenamiento
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Resumen Principal */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-cyan-500/10 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-cyan-300">
                {report.strength?.total_sessions}
              </p>
              <p className="text-[10px] text-cyan-300/70">Sesiones</p>
            </div>
            <div className="bg-cyan-500/10 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-cyan-300">
                {Math.round(report.strength?.total_volume_kg / 1000 * 10) / 10}t
              </p>
              <p className="text-[10px] text-cyan-300/70">Toneladas</p>
            </div>
            <div className="bg-cyan-500/10 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-cyan-300">
                {report.strength?.adherence_percent}%
              </p>
              <p className="text-[10px] text-cyan-300/70">Adherencia</p>
            </div>
          </div>

          {/* Análisis de Progresión */}
          <div className="bg-black/30 rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-3">Progresión de ejercicios:</p>
            <div className="flex gap-2">
              <div className="flex-1 bg-green-500/20 rounded p-2 text-center">
                <TrendingUp className="h-4 w-4 text-green-400 mx-auto mb-1" />
                <p className="text-lg font-bold text-green-400">{report.strength?.exercises_improved || 0}</p>
                <p className="text-[9px] text-green-300/70">Mejoraron</p>
              </div>
              <div className="flex-1 bg-yellow-500/20 rounded p-2 text-center">
                <Target className="h-4 w-4 text-yellow-400 mx-auto mb-1" />
                <p className="text-lg font-bold text-yellow-400">{report.strength?.exercises_maintained || 0}</p>
                <p className="text-[9px] text-yellow-300/70">Mantuvieron</p>
              </div>
              <div className="flex-1 bg-red-500/20 rounded p-2 text-center">
                <TrendingDown className="h-4 w-4 text-red-400 mx-auto mb-1" />
                <p className="text-lg font-bold text-red-400">{report.strength?.exercises_decreased || 0}</p>
                <p className="text-[9px] text-red-300/70">Bajaron</p>
              </div>
            </div>
          </div>

          {/* Top Ejercicios */}
          {report.strength?.top_exercises?.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground flex items-center gap-2">
                <Flame className="h-4 w-4 text-orange-400" />
                Top Ejercicios por Volumen:
              </p>
              {report.strength.top_exercises.slice(0, 3).map((ex, idx) => (
                <div key={idx} className="flex items-center justify-between bg-black/20 rounded-lg p-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}</span>
                    <span className="text-sm text-foreground">{ex.name}</span>
                  </div>
                  <span className="text-sm text-cyan-400">{Math.round(ex.volume_kg)} kg</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* SECCIÓN MENTE */}
      <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/5 border-purple-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-purple-400 flex items-center gap-2">
            <Brain className="h-5 w-5" />
            MENTE - Educación y Conocimiento
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Píldoras Formativas */}
          <div className="bg-black/30 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-foreground flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-purple-400" />
                Píldoras Formativas
              </span>
              <span className="text-sm text-purple-400">
                {report.mind?.pildoras_formativas?.completed || 0}/{report.mind?.pildoras_formativas?.total || 10}
              </span>
            </div>
            <Progress 
              value={((report.mind?.pildoras_formativas?.completed || 0) / (report.mind?.pildoras_formativas?.total || 10)) * 100} 
              className="h-2"
            />
          </div>

          {/* Nutrición */}
          {report.mind?.nutricion?.has_access && (
            <div className="bg-black/30 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-foreground flex items-center gap-2">
                  <Zap className="h-4 w-4 text-green-400" />
                  Módulo Nutrición
                </span>
                <span className="text-sm text-green-400">
                  {report.mind.nutricion.completed || 0}/{report.mind.nutricion.total || 10}
                </span>
              </div>
              <Progress 
                value={((report.mind?.nutricion?.completed || 0) / (report.mind?.nutricion?.total || 10)) * 100} 
                className="h-2"
              />
              {report.mind.nutricion.tools_unlocked && (
                <p className="text-xs text-green-400 mt-2 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Generador de Menús desbloqueado
                </p>
              )}
            </div>
          )}

          {/* Objetivo Guardián */}
          {report.mind?.guardian?.status !== 'locked' && (
            <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-lg p-3 border border-amber-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-amber-400" />
                <span className="text-sm text-amber-400 font-medium">Aspirante Guardián</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-lg font-bold text-foreground">{report.mind.guardian.lessons_completed}</p>
                  <p className="text-[9px] text-muted-foreground">Lecciones</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-foreground">{report.mind.guardian.logs_completed}</p>
                  <p className="text-[9px] text-muted-foreground">Registros</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-foreground">S{report.mind.guardian.current_week}D{report.mind.guardian.current_day}</p>
                  <p className="text-[9px] text-muted-foreground">Progreso</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* SECCIÓN CONTROL */}
      <Card className="bg-gradient-to-br from-indigo-500/10 to-violet-500/5 border-indigo-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-indigo-400 flex items-center gap-2">
            <Moon className="h-5 w-5" />
            CONTROL - Descansos Conscientes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-black/30 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-indigo-300">{report.control?.discipline_score || 0}%</p>
              <p className="text-xs text-muted-foreground mt-1">Disciplina</p>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Completados</span>
                <span className="text-green-400">{report.control?.completed_rests || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Abandonados</span>
                <span className="text-red-400">{report.control?.abandoned_rests || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Programa</span>
                <span className="text-indigo-400">{report.control?.program_name}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Próximo Reto */}
      <Card className="bg-gradient-to-r from-primary/10 to-amber-500/10 border-primary/30">
        <CardContent className="py-4">
          <p className="text-xs text-muted-foreground mb-1">Tu próximo reto:</p>
          <p className="text-foreground font-medium">{report.next_challenge}</p>
        </CardContent>
      </Card>

      {/* Botones de Acción */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-background via-background to-transparent">
        <div className="max-w-2xl mx-auto space-y-2">
          {/* Progreso actual */}
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">Progreso: {report.progress_percent}%</span>
            {!report.can_finalize && (
              <span className="text-amber-400">Mínimo 80% para finalizar</span>
            )}
          </div>
          <Progress value={report.progress_percent} className="h-2 mb-3" />
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={downloadReport}
            >
              <Download className="h-4 w-4 mr-2" />
              Descargar
            </Button>
            
            {report.can_finalize ? (
              <Button
                className="flex-1 bg-gradient-to-r from-primary to-amber-500 text-black font-bold"
                onClick={handleFinalize}
                disabled={finalizing}
              >
                {finalizing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Trophy className="h-4 w-4 mr-2" />
                )}
                Finalizar Mesociclo
              </Button>
            ) : (
              <Button
                variant="outline"
                className="flex-1"
                onClick={onClose}
              >
                Seguir Entrenando
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MesocycleReport;
